/**
 * Custom Sticker Builder Logic
 */
class StickerBuilder {
    constructor(config) {
        this.maxStickers = config.maxStickers || 45; // Support up to 45 stickers
        this.collectionHandle = config.collectionHandle;
        this.selectedStickers = [];
        this.allStickers = config.products || [];
        this.itemsPerPage = 80;
        this.currentPage = 1;

        // DOM Elements
        this.grid = document.getElementById('sticker-grid');
        this.searchInput = document.getElementById('sticker-search');
        this.filterTags = document.querySelectorAll('.filter-tag');
        this.selectedCountDisplay = document.querySelector('.selected-count');
        this.progressBar = document.querySelector('.progress-bar');
        this.laptopStickersContainer = document.querySelector('.stickers-on-laptop');
        this.buyNowBtn = document.getElementById('buy-now-btn');
        this.clearBtn = document.getElementById('clear-selection');
        this.mobileTrigger = document.getElementById('view-sheet-trigger');
        this.previewClose = document.getElementById('preview-close');
        this.previewCard = document.getElementById('preview-card');
        this.previewOverlay = document.getElementById('preview-overlay');

        // Bundle Promo DOM Elements
        this.promoBox = document.querySelector('.promo-bundle-box');
        this.promoMessage = document.getElementById('promo-message');
        this.promoProgress = document.getElementById('promo-progress');
        this.builderSection = document.querySelector('.sticker-builder-section');

        this.init();
    }

    init() {
        this.renderGrid(this.allStickers, 1);
        this.setupEventListeners();
        this.updateUI();
    }

    setupEventListeners() {
        // Search
        this.searchInput.addEventListener('input', (e) => {
            this.handleSearch(e.target.value);
        });

        // Filters
        this.filterTags.forEach(tag => {
            tag.addEventListener('click', () => {
                this.filterTags.forEach(t => t.classList.remove('active'));
                tag.classList.add('active');
                this.handleFilter(tag.dataset.filter);
            });
        });

        // Click actions (delegated)
        this.grid.addEventListener('click', (e) => {
            const addBtn = e.target.closest('.add-btn');
            const qtyMinus = e.target.closest('.qty-minus');
            const qtyPlus = e.target.closest('.qty-plus');
            const paginationBtn = e.target.closest('.pagination-btn');

            if (addBtn) {
                this.increaseQty(addBtn.dataset.id);
            } else if (qtyMinus) {
                this.decreaseQty(qtyMinus.dataset.id);
            } else if (qtyPlus) {
                this.increaseQty(qtyPlus.dataset.id);
            } else if (paginationBtn && !paginationBtn.disabled) {
                const newPage = parseInt(paginationBtn.dataset.page);
                this.renderGrid(this.getCurrentFilteredProducts(), newPage);
                this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });

        // Clear
        this.clearBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.clearSelection();
        });

        // Buy Now
        this.buyNowBtn.addEventListener('click', () => {
            this.handleCheckout();
        });

        // Mobile Toggle
        if (this.mobileTrigger) {
            this.mobileTrigger.addEventListener('click', () => {
                this.previewCard.classList.add('is-open');
                this.previewOverlay.classList.add('is-active');
                document.body.style.overflow = 'hidden'; 
            });
        }

        if (this.previewClose) {
            this.previewClose.addEventListener('click', () => {
                this.previewCard.classList.remove('is-open');
                this.previewOverlay.classList.remove('is-active');
                document.body.style.overflow = '';
            });
        }

        if (this.previewOverlay) {
            this.previewOverlay.addEventListener('click', () => {
                this.previewCard.classList.remove('is-open');
                this.previewOverlay.classList.remove('is-active');
                document.body.style.overflow = '';
            });
        }
    }

    renderGrid(products, page = this.currentPage) {
        this.currentPage = page;
        const totalPages = Math.ceil(products.length / this.itemsPerPage) || 1;
        const startIndex = (page - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        const pageProducts = products.slice(startIndex, endIndex);

        let html = pageProducts.map(product => {
            const qty = this.getQuantity(product.id);
            const isSelected = qty > 0;
            const isSoldOut = !product.available;
            const stockLimit = this.getStockLimit(product);
            const atStockLimit = stockLimit !== null && qty >= stockLimit;
            const atGlobalLimit = this.selectedStickers.length >= this.maxStickers;
            const plusDisabled = atStockLimit || atGlobalLimit;

            // Stock label shown below image when stock is low (≤5 and not sold out)
            const lowStock = !isSoldOut && stockLimit !== null && stockLimit <= 5;
            const stockLabel = isSoldOut
                ? `<span class="stock-badge sold-out-badge">SOLD OUT</span>`
                : lowStock
                ? `<span class="stock-badge low-stock-badge">Only ${stockLimit - qty} left</span>`
                : '';

            const actionHtml = isSoldOut
                ? `<button class="add-btn sold-out-btn" disabled>Sold Out</button>`
                : isSelected
                ? `
                    <div class="qty-selector">
                        <button class="qty-btn qty-minus" data-id="${product.id}">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                        <span class="qty-count">${qty}</span>
                        <button class="qty-btn qty-plus ${plusDisabled ? 'qty-plus-disabled' : ''}" data-id="${product.id}" ${plusDisabled ? 'disabled' : ''}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        </button>
                    </div>`
                : `<button class="add-btn" data-id="${product.id}">Add to Sheet</button>`;

            return `
                <div class="sticker-item ${isSelected ? 'selected' : ''} ${isSoldOut ? 'is-sold-out' : ''}" data-id="${product.id}" data-type="${product.type}">
                    <div class="quantity-badge" style="${isSelected && !isSoldOut ? 'display:flex' : 'display:none'}">${qty}</div>
                    ${stockLabel}
                    <img src="${product.image}" alt="${product.title}" class="sticker-image ${isSoldOut ? 'sold-out-img' : ''}">
                    <span class="category">${product.type || 'Sticker'}</span>
                    <h3>${product.title}</h3>
                    ${actionHtml}
                </div>
            `;
        }).join('');

        if (totalPages > 1) {
            html += `
                <div class="pagination-controls" style="grid-column: 1 / -1; display: flex; justify-content: center; gap: 15px; margin-top: 30px; align-items: center; width: 100%; padding-bottom: 20px;">
                    <button class="pagination-btn prev-btn" ${page === 1 ? 'disabled' : ''} data-page="${page - 1}" style="padding: 8px 16px; border: 1px solid #ddd; background: ${page === 1 ? '#f5f5f5' : '#fff'}; color: ${page === 1 ? '#999' : '#333'}; cursor: ${page === 1 ? 'not-allowed' : 'pointer'}; border-radius: 4px; font-weight: 600;">Previous</button>
                    <span style="font-size: 14px; font-weight: 500;">Page ${page} of ${totalPages}</span>
                    <button class="pagination-btn next-btn" ${page === totalPages ? 'disabled' : ''} data-page="${page + 1}" style="padding: 8px 16px; border: 1px solid #ddd; background: ${page === totalPages ? '#f5f5f5' : '#fff'}; color: ${page === totalPages ? '#999' : '#333'}; cursor: ${page === totalPages ? 'not-allowed' : 'pointer'}; border-radius: 4px; font-weight: 600;">Next</button>
                </div>
            `;
        }

        this.grid.innerHTML = html || '<div class="no-results" style="grid-column: 1 / -1; text-align: center; padding: 20px;">No stickers found.</div>';
    }

    handleSearch(query) {
        const activeFilter = document.querySelector('.filter-tag.active').dataset.filter;
        const filtered = this.allStickers.filter(p => {
            const matchesSearch = p.title.toLowerCase().includes(query.toLowerCase());
            const productTags = p.tags ? p.tags.map(t => t.toLowerCase()) : [];
            const matchesFilter = activeFilter === 'all' || productTags.includes(activeFilter.toLowerCase());
            return matchesSearch && matchesFilter;
        });
        this.renderGrid(filtered, 1);
    }

    handleFilter(filter) {
        const query = this.searchInput.value;
        const filtered = this.allStickers.filter(p => {
            const matchesSearch = p.title.toLowerCase().includes(query.toLowerCase());
            const productTags = p.tags ? p.tags.map(t => t.toLowerCase()) : [];
            const matchesFilter = filter === 'all' || productTags.includes(filter.toLowerCase());
            return matchesSearch && matchesFilter;
        });
        this.renderGrid(filtered, 1);
    }

    increaseQty(productId) {
        if (this.selectedStickers.length >= this.maxStickers) {
            alert(`You can only select up to ${this.maxStickers} stickers.`);
            return;
        }

        const product = this.allStickers.find(p => p.id == productId);
        if (!product) return;

        // Check per-product stock limit
        const stockLimit = this.getStockLimit(product);
        const currentQty = this.getQuantity(productId);
        if (stockLimit !== null && currentQty >= stockLimit) {
            alert(`Only ${stockLimit} unit${stockLimit > 1 ? 's' : ''} of "${product.title}" available in stock.`);
            return;
        }

        this.selectedStickers.push(product);
        this.updateUI();
        this.renderGrid(this.getCurrentFilteredProducts(), this.currentPage);
        this.addStickerToLaptop(product);
    }

    // Returns the max qty a user can add for a product, or null if unlimited
    getStockLimit(product) {
        if (!product.available) return 0; // sold out
        if (product.inventoryPolicy === 'continue') return null; // oversell allowed
        if (product.inventoryQuantity !== null && product.inventoryQuantity >= 0) {
            return product.inventoryQuantity;
        }
        return null; // no inventory tracking
    }

    decreaseQty(productId) {
        const index = this.selectedStickers.findLastIndex(s => s.id == productId);
        if (index !== -1) {
            this.selectedStickers.splice(index, 1);
            this.updateUI();
            this.renderGrid(this.getCurrentFilteredProducts(), this.currentPage);
            this.removeLastStickerFromPreview(productId);
        }
    }

    getCurrentFilteredProducts() {
        const query = this.searchInput.value;
        const activeFilter = document.querySelector('.filter-tag.active').dataset.filter;
        return this.allStickers.filter(p => {
            const matchesSearch = p.title.toLowerCase().includes(query.toLowerCase());
            const productTags = p.tags ? p.tags.map(t => t.toLowerCase()) : [];
            const matchesFilter = activeFilter === 'all' || productTags.includes(activeFilter.toLowerCase());
            return matchesSearch && matchesFilter;
        });
    }

    isSelected(productId) {
        return this.selectedStickers.some(s => s.id == productId);
    }

    getQuantity(productId) {
        return this.selectedStickers.filter(s => s.id == productId).length;
    }

    getPricingInfo(count) {
        let priceCents = 0;
        let activeTier = null;
        let nextTier = null;
        
        if (count < 15) {
            priceCents = this.selectedStickers.reduce((sum, s) => sum + s.price, 0);
            nextTier = { count: 15, price: 19900, label: '₹199' };
        } else if (count === 15) {
            priceCents = 19900;
            activeTier = { count: 15, price: 19900, label: '₹199' };
            nextTier = { count: 30, price: 34900, label: '₹349' };
        } else if (count < 30) {
            const basePrice = 19900;
            const excessPrice = this.selectedStickers.slice(15).reduce((sum, s) => sum + s.price, 0);
            priceCents = basePrice + excessPrice;
            activeTier = { count: 15, price: 19900, label: '₹199' };
            nextTier = { count: 30, price: 34900, label: '₹349' };
        } else if (count === 30) {
            priceCents = 34900;
            activeTier = { count: 30, price: 34900, label: '₹349' };
            nextTier = { count: 45, price: 52900, label: '₹529' };
        } else if (count < 45) {
            const basePrice = 34900;
            const excessPrice = this.selectedStickers.slice(30).reduce((sum, s) => sum + s.price, 0);
            priceCents = basePrice + excessPrice;
            activeTier = { count: 30, price: 34900, label: '₹349' };
            nextTier = { count: 45, price: 52900, label: '₹529' };
        } else if (count === 45) {
            priceCents = 52900;
            activeTier = { count: 45, price: 52900, label: '₹529' };
        } else {
            const basePrice = 52900;
            const excessPrice = this.selectedStickers.slice(45).reduce((sum, s) => sum + s.price, 0);
            priceCents = basePrice + excessPrice;
            activeTier = { count: 45, price: 52900, label: '₹529' };
        }

        return { priceCents, activeTier, nextTier };
    }

    updateUI() {
        const count = this.selectedStickers.length;
        
        // Dynamic DOM element retrieval to avoid detached/null nodes
        const promoProgress = document.getElementById('promo-progress');
        const promoBox = document.querySelector('.promo-bundle-box');
        const promoMessage = document.getElementById('promo-message');
        const builderSection = document.querySelector('.sticker-builder-section');
        const selectedCountDisplay = document.querySelector('.selected-count');
        const buyNowBtn = document.getElementById('buy-now-btn');

        if (selectedCountDisplay) {
            selectedCountDisplay.textContent = `${count} / ${this.maxStickers}`;
        }

        // Handle selection maximum styling on the main page section
        if (builderSection) {
            if (count >= this.maxStickers) {
                builderSection.classList.add('max-reached');
            } else {
                builderSection.classList.remove('max-reached');
            }
        }

        // Handle promotional progress bar
        if (promoProgress) {
            let progress = 0;
            if (count < 15) {
                progress = (count / 15) * 100;
            } else if (count < 30) {
                progress = ((count - 15) / 15) * 100;
            } else if (count < 45) {
                progress = ((count - 30) / 15) * 100;
            } else {
                progress = 100;
            }
            promoProgress.style.width = `${progress}%`;
        }

        const sumPriceCents = this.selectedStickers.reduce((sum, s) => sum + s.price, 0);
        const { priceCents, activeTier, nextTier } = this.getPricingInfo(count);

        // Handle promotional box messages and styling classes
        if (promoBox && promoMessage) {
            // Update tier card states
            const tier15 = document.getElementById('tier-15');
            const tier30 = document.getElementById('tier-30');
            const tier45 = document.getElementById('tier-45');

            if (tier15) tier15.classList.toggle('unlocked', count >= 15);
            if (tier30) tier30.classList.toggle('unlocked', count >= 30);
            if (tier45) tier45.classList.toggle('unlocked', count >= 45);
            if (tier15) tier15.classList.toggle('active', count > 0 && count < 15);
            if (tier30) tier30.classList.toggle('active', count >= 15 && count < 30);
            if (tier45) tier45.classList.toggle('active', count >= 30 && count < 45);

            if (count === 0) {
                promoBox.classList.remove('unlocked');
                promoMessage.innerHTML = 'Add <strong>15 stickers</strong> to unlock your first bundle at <strong>₹199</strong>!';
            } else if (count < 15) {
                promoBox.classList.remove('unlocked');
                const remaining = 15 - count;
                promoMessage.innerHTML = `Add <strong>${remaining}</strong> more sticker${remaining > 1 ? 's' : ''} to unlock <strong>15 stickers @ ₹199</strong>!`;
            } else if (count === 15) {
                promoBox.classList.add('unlocked');
                promoMessage.innerHTML = `<strong>15-pack unlocked @ ₹199!</strong> Add <strong>15 more</strong> to upgrade to 30 stickers for <strong>₹349</strong>!`;
            } else if (count < 30) {
                promoBox.classList.remove('unlocked');
                const remaining = 30 - count;
                promoMessage.innerHTML = `Add <strong>${remaining}</strong> more sticker${remaining > 1 ? 's' : ''} to upgrade to <strong>30 stickers @ ₹349</strong>!`;
            } else if (count === 30) {
                promoBox.classList.add('unlocked');
                promoMessage.innerHTML = `<strong>30-pack unlocked @ ₹349!</strong> Add <strong>15 more</strong> to upgrade to 45 stickers for <strong>₹529</strong>!`;
            } else if (count < 45) {
                promoBox.classList.remove('unlocked');
                const remaining = 45 - count;
                promoMessage.innerHTML = `Add <strong>${remaining}</strong> more sticker${remaining > 1 ? 's' : ''} to unlock the <strong>Mega Pack — 45 stickers @ ₹529</strong>!`;
            } else {
                promoBox.classList.add('unlocked');
                promoMessage.innerHTML = `<strong>Mega Pack unlocked!</strong> You're getting <strong>45 stickers for just ₹529</strong>!`;
            }
        }

        // Calculate and Display Price
        const totalPriceDisplay = document.getElementById('total-price');

        if (totalPriceDisplay) {
            if (priceCents < sumPriceCents) {
                const originalFormatted = window.Shopify && window.Shopify.formatMoney 
                    ? window.Shopify.formatMoney(sumPriceCents) 
                    : `₹${(sumPriceCents / 100).toLocaleString('en-IN')}`;
                
                const discountedFormatted = window.Shopify && window.Shopify.formatMoney 
                    ? window.Shopify.formatMoney(priceCents) 
                    : `₹${(priceCents / 100).toLocaleString('en-IN')}`;

                totalPriceDisplay.innerHTML = `
                    <span class="original-price-strike">${originalFormatted}</span>
                    <span class="bundle-price-active">${discountedFormatted}</span>
                `;
            } else {
                const formattedPrice = window.Shopify && window.Shopify.formatMoney 
                    ? window.Shopify.formatMoney(priceCents) 
                    : `₹${(priceCents / 100).toLocaleString('en-IN')}`;
                
                totalPriceDisplay.innerHTML = `<span>${formattedPrice}</span>`;
            }
        }

        if (buyNowBtn) {
            buyNowBtn.disabled = count === 0;
        }
    }

    addStickerToLaptop(product) {
        const stickerImg = document.createElement('img');
        stickerImg.src = product.image;
        stickerImg.className = 'sticker-on-preview';
        stickerImg.dataset.productId = product.id;

        // Random position on white canvas
        const top = 10 + Math.random() * 70;
        const left = 10 + Math.random() * 70;
        const rotation = (Math.random() - 0.5) * 60;

        stickerImg.style.top = `${top}%`;
        stickerImg.style.left = `${left}%`;
        stickerImg.style.transform = `rotate(${rotation}deg) scale(0)`;

        this.laptopStickersContainer.appendChild(stickerImg);

        // Animate in
        setTimeout(() => {
            stickerImg.style.transform = `rotate(${rotation}deg) scale(1)`;
        }, 50);
    }

    removeLastStickerFromPreview(productId) {
        const stickers = Array.from(this.laptopStickersContainer.querySelectorAll(`.sticker-on-preview[data-product-id="${productId}"]`));
        if (stickers.length > 0) {
            const last = stickers[stickers.length - 1];
            last.style.transform += ' scale(0)';
            setTimeout(() => last.remove(), 300);
        }
    }

    clearSelection() {
        this.selectedStickers = [];
        this.laptopStickersContainer.innerHTML = '';
        this.updateUI();
        this.renderGrid(this.allStickers, 1);
    }

    async handleCheckout() {
        this.buyNowBtn.disabled = true;
        this.buyNowBtn.textContent = 'Adding to Cart...';

        try {
            // Validate all selected stickers have a valid variantId
            const invalidStickers = this.selectedStickers.filter(s => !s.variantId);
            if (invalidStickers.length > 0) {
                const names = invalidStickers.map(s => s.title).join(', ');
                alert(`Some stickers could not be added (no variant found): ${names}`);
                this.buyNowBtn.disabled = false;
                this.buyNowBtn.textContent = 'BUY NOW';
                return;
            }

            // Group stickers by variantId
            const items = [];
            const grouped = {};
            this.selectedStickers.forEach(s => {
                const variantId = s.variantId;
                if (!grouped[variantId]) {
                    grouped[variantId] = 0;
                }
                grouped[variantId]++;
            });
            Object.keys(grouped).forEach(id => {
                items.push({
                    id: parseInt(id),
                    quantity: grouped[id]
                });
            });

            // Get sections to bundle/render for cart drawer update
            let sectionsToBundle = [];
            document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

            const body = JSON.stringify({
                items: items,
                sections: sectionsToBundle,
                sections_url: window.location.pathname
            });

            const response = await fetch(`${window.theme.routes.cart_add_url}`, {
                ...window.theme.utils.fetchConfig('javascript'),
                body
            });
            const parsedState = await response.json();

            if (parsedState.status) {
                // If there's an error from Shopify (e.g. out of stock)
                console.error('Add to cart error:', parsedState.description);
                alert(parsedState.description || 'Could not add items to cart.');
                this.buyNowBtn.disabled = false;
                this.buyNowBtn.textContent = 'BUY NOW';
                return;
            }

            // Get updated cart state & sections
            const cartResponse = await fetch(`${window.theme.routes.cart_url}`, {
                ...window.theme.utils.fetchConfig()
            });
            const cartJson = await cartResponse.json();
            cartJson['sections'] = parsedState['sections'];

            // Publish cart update event to let CartDrawer/Header update
            window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                cart: cartJson
            });

            // Dispatch custom event for theme/other apps
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
                detail: {
                    product: parsedState
                }
            }));

            // Show cart drawer
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
                cartDrawer.show(this.buyNowBtn);
            } else {
                // Fallback to reload or cart page if drawer is not present
                window.location.href = window.theme.routes.cart_url;
            }

            // Reset selection after successfully adding to cart
            this.clearSelection();
            this.buyNowBtn.textContent = 'BUY NOW';

        } catch (err) {
            console.error('Add to cart error:', err);
            alert('Something went wrong. Please try again.');
            this.buyNowBtn.disabled = false;
            this.buyNowBtn.textContent = 'BUY NOW';
        }
    }
}

window.StickerBuilder = StickerBuilder;
